/**
 * PlayerSoundManager - Manages player-related sound effects
 *
 * Handles continuous/state-based sounds like footsteps that need to:
 * - Loop while a condition is met
 * - Start/stop based on player state
 * - Adjust based on player velocity
 *
 * And one-shots like the drift slide, which fire on the rising edge of
 * `player.drift.active` (once per slide, not while the key is held).
 */

import { ClientLogger } from '../logger/ClientLogger.js';
import { resolveSound } from '../../shared/resolveSound.js';

const playerSoundLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {} };
})();
const playerSoundLogger = ClientLogger.getLogger('audio') || playerSoundLoggerFallback;

class PlayerSoundManager {
  constructor(config) {
    this.config = config || {};

    // Audio elements for looping sounds
    this.sounds = {
      footsteps: null,
      slide: null
    };

    // State tracking
    this.isMoving = false;
    this.footstepsPlaying = false;
    this.wasDrifting = false;
    this.spatialAudio = null;
    this.footstepsConfig = null;
    this._footstepsHandle = null;
    this._footstepsStartId = 0;
    this._footstepsPlaybackRate = 1.0;
  }

  /**
   * Initialize audio elements
   * Call this after config is loaded
   * @param {Object} playerConfig - Player config (`sounds.footsteps`, `sounds.slide`)
   * @param {Object} [spatialAudio] - SpatialAudioManager; looping footsteps use its
   *   Web Audio path so held movement does not gap at HTML Audio loop seams
   */
  initialize(playerConfig, spatialAudio = null) {
    this.spatialAudio = spatialAudio;

    if (!playerConfig?.sounds?.footsteps) {
      playerSoundLogger.warn('[PlayerSoundManager] No footsteps config found');
    } else {
      const footstepsConfig = playerConfig.sounds.footsteps;
      const resolved = resolveSound(footstepsConfig);
      this.footstepsConfig = resolved
        ? {
            ...(typeof footstepsConfig === 'object' ? footstepsConfig : {}),
            path: resolved.path,
            volume: resolved.volume
          }
        : footstepsConfig;
      this.velocityThreshold = footstepsConfig.velocityThreshold || 0.5;
      this._footstepsPlaybackRate = footstepsConfig.playbackRate || 1.0;

      if (spatialAudio?.playLoopingLocal) {
        this.sounds.footsteps = { path: this.footstepsConfig.path };
        if (typeof spatialAudio.preload === 'function') {
          spatialAudio.preload([this.footstepsConfig.path]).catch((err) => {
            playerSoundLogger.warn('[PlayerSoundManager] Failed to preload footsteps:', err);
          });
        }
      } else if (this.footstepsConfig.path) {
        this.sounds.footsteps = new Audio(this.footstepsConfig.path);
        this.sounds.footsteps.loop = footstepsConfig.loop !== false;
        this.sounds.footsteps.volume = this.footstepsConfig.volume;
        this.sounds.footsteps.playbackRate = this._footstepsPlaybackRate;
      }

      playerSoundLogger.info('[PlayerSoundManager] Initialized with config:', this.footstepsConfig);
    }

    const slideResolved = resolveSound(playerConfig?.sounds?.slide);
    if (slideResolved) {
      this.sounds.slide = new Audio(slideResolved.path);
      this.sounds.slide.loop = false;
      this.sounds.slide.volume = slideResolved.volume;
    }
  }

  /**
   * Update sound state based on player velocity and drift
   * Call this in the game loop
   * @param {Object} player - Local player object
   */
  update(player) {
    if (!player) return;

    this._updateSlide(player);

    if (!this.sounds.footsteps) return;

    // Calculate player speed (magnitude of velocity)
    const vx = player.vx || 0;
    const vy = player.vy || 0;
    const speed = Math.sqrt(vx * vx + vy * vy);

    // Determine if player is moving
    const isMoving = speed > this.velocityThreshold;
    const drifting = !!player.drift?.active;
    const shouldPlayFootsteps = isMoving && !drifting;

    // Adjust footstep playback rate based on SuperSpeed ability
    const speedMultiplier = player.activeAbilityEffects?.speedMultiplier || 1.0;
    this.setFootstepsPlaybackRate(speedMultiplier);

    // Handle state transitions. Retry start while moving so a failed Web Audio
    // init (autoplay, still-loading buffer) does not stay silent until a release.
    // Drift uses the slide one-shot; footsteps must not overlap it.
    if (shouldPlayFootsteps) {
      if (!this.footstepsPlaying) this.startFootsteps();
    } else if (this.footstepsPlaying) {
      this.stopFootsteps();
    }

    this.isMoving = isMoving;
  }

  /**
   * One-shot slide on the tick drift becomes active.
   * @private
   */
  _updateSlide(player) {
    const active = !!player.drift?.active;
    if (active && !this.wasDrifting) {
      this._playSlide();
    }
    this.wasDrifting = active;
  }

  /**
   * @private
   */
  _playSlide() {
    if (!this.sounds.slide) return;
    this.sounds.slide.currentTime = 0;
    this.sounds.slide.play().catch((err) => {
      playerSoundLogger.warn('[PlayerSoundManager] Failed to play slide:', err);
    });
  }

  /**
   * Start playing footsteps
   */
  startFootsteps() {
    if (!this.sounds.footsteps || this.footstepsPlaying) return;

    this.footstepsPlaying = true;

    if (this.spatialAudio?.playLoopingLocal) {
      const startId = ++this._footstepsStartId;
      const volume = this.footstepsConfig?.volume ?? 1;
      this.spatialAudio.playLoopingLocal(this.footstepsConfig.path, {
        volume,
        playbackRate: this._footstepsPlaybackRate
      }).then((handle) => {
        if (!handle || !this.footstepsPlaying || startId !== this._footstepsStartId) {
          try {
            handle?.source?.stop();
          } catch (err) {
            // Already stopped
          }
          return;
        }
        this._footstepsHandle = handle;
        handle.source.playbackRate.value = this._footstepsPlaybackRate;
      }).catch((err) => {
        playerSoundLogger.warn('[PlayerSoundManager] Failed to play footsteps:', err);
        if (startId === this._footstepsStartId) {
          this.footstepsPlaying = false;
        }
      });
      return;
    }

    this.sounds.footsteps.currentTime = 0;
    this.sounds.footsteps.play().catch(err => {
      playerSoundLogger.warn('[PlayerSoundManager] Failed to play footsteps:', err);
    });
  }

  /**
   * Stop playing footsteps
   */
  stopFootsteps() {
    if (!this.sounds.footsteps || !this.footstepsPlaying) return;

    this.footstepsPlaying = false;
    this._footstepsStartId += 1;

    if (this._footstepsHandle?.source) {
      try {
        this._footstepsHandle.source.stop();
      } catch (err) {
        // Already stopped
      }
      this._footstepsHandle = null;
    }

    if (typeof this.sounds.footsteps.pause === 'function') {
      this.sounds.footsteps.pause();
    }
  }

  /**
   * Set footsteps volume
   * @param {number} volume - Volume from 0.0 to 1.0
   */
  setFootstepsVolume(volume) {
    const clamped = Math.max(0, Math.min(1, volume));
    if (this.footstepsConfig) {
      this.footstepsConfig = { ...this.footstepsConfig, volume: clamped };
    }
    if (this._footstepsHandle?.gainNode) {
      this._footstepsHandle.gainNode.gain.value = clamped;
    }
    if (this.sounds.footsteps && typeof this.sounds.footsteps.volume === 'number') {
      this.sounds.footsteps.volume = clamped;
    }
  }

  /**
   * Set footsteps playback rate (speed)
   * @param {number} rate - Playback rate (1.0 = normal speed)
   */
  setFootstepsPlaybackRate(rate) {
    this._footstepsPlaybackRate = rate;
    if (this._footstepsHandle?.source) {
      this._footstepsHandle.source.playbackRate.value = rate;
    }
    if (this.sounds.footsteps && typeof this.sounds.footsteps.playbackRate === 'number') {
      this.sounds.footsteps.playbackRate = rate;
    }
  }

  /**
   * Stop all active player sounds (footsteps, slide)
   */
  stopAll() {
    this.stopFootsteps();
    if (this.sounds.slide && typeof this.sounds.slide.pause === 'function') {
      try {
        this.sounds.slide.pause();
        this.sounds.slide.currentTime = 0;
      } catch (err) {
        // Already stopped
      }
    }
    this.wasDrifting = false;
  }

  /**
   * Clean up - stop all sounds
   */
  destroy() {
    this.stopFootsteps();

    // Release audio elements
    if (this.sounds.footsteps) {
      if ('src' in this.sounds.footsteps) {
        this.sounds.footsteps.src = '';
      }
      this.sounds.footsteps = null;
    }
    if (this.sounds.slide) {
      this.sounds.slide.src = '';
      this.sounds.slide = null;
    }
    this.wasDrifting = false;
  }
}

// Export for use in client.js

export { playerSoundLoggerFallback, playerSoundLogger, PlayerSoundManager };
